/**
 * Market currency code
 *
 * Appends the ISO currency code to rendered prices ("$50.00" -> "$50.00 USD")
 * for visitors on the international catalogue, where a bare "$" is ambiguous —
 * a shopper in Canada reading "$50.00" may reasonably assume CAD.
 *
 * Why this is a DOM pass and not a Liquid change:
 * prices on this store are produced by theme Liquid, theme JS (variant-picker,
 * product-card-swatch, cart drawer), GemPages, PageFly and Replo. There is no
 * single Liquid seam that covers all of them, and Shopify has no per-market
 * money format. Patching the rendered text is the only mechanism that reaches
 * every surface.
 *
 * Configuration comes from snippets/market-currency-code.liquid via
 * window.__marketCurrencyCode. That snippet only emits this script for markets
 * that need it, so there is no market check in here.
 *
 * Idempotency: a processed price ends in letters (" USD") and therefore no
 * longer matches PRICE_TEXT. Re-running the pass — or the MutationObserver
 * seeing our own writes — cannot double-append or loop.
 */
(function () {
  'use strict';

  var config = window.__marketCurrencyCode;
  if (!config || !config.code) return;

  var CODE = config.code;
  var SYMBOL = config.symbol || '$';

  /**
   * Subtrees whose prices stay bare. Unit prices are excluded because the code
   * lands in the middle of the string ("$5.00 USD/oz"). Add selectors here if
   * QA turns up a surface where the code reads badly — this is the tuning knob.
   */
  var SKIP_SELECTORS = [
    '[data-no-currency-code]',
    '.m-price__unit',
    '.m-price__unit-wrapper',
    'script',
    'style',
    'noscript',
    'template',
    'textarea',
    'select',
    'svg',
    '[contenteditable]',
  ].join(',');

  var escapedSymbol = SYMBOL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // "$50", "$1,299.00", "$ 50.00"
  var AMOUNT = escapedSymbol + '\\s?\\d{1,3}(?:[.,\\s]?\\d{3})*(?:[.,]\\d{2})?';

  /**
   * A text node is treated as a price only when it consists of nothing but a
   * money amount, optionally preceded by a single word ("From $50.00") and
   * optionally a range ("$50.00 – $70.00").
   *
   * Anchoring both ends is what keeps this safe: prose that merely mentions a
   * figure ("Spend $50 to unlock free shipping") never matches, and neither
   * does an amount that already carries a code ("$50.00 USD").
   */
  var PRICE_TEXT = new RegExp(
    '^\\s*' +
      '(?:[^\\d\\s' +
      escapedSymbol +
      ']{1,12}\\s+)?' +
      AMOUNT +
      '(?:\\s*[-–—]\\s*' +
      AMOUNT +
      ')?' +
      '\\s*$'
  );

  function isSkipped(element) {
    if (!element || element.nodeType !== 1) return true;
    if (element.isContentEditable) return true;
    return typeof element.closest === 'function' && !!element.closest(SKIP_SELECTORS);
  }

  function processTextNode(node) {
    var text = node.nodeValue;
    if (!text || text.indexOf(SYMBOL) === -1) return;
    if (!PRICE_TEXT.test(text)) return;
    if (isSkipped(node.parentNode)) return;

    node.nodeValue = text.replace(/\s+$/, '') + ' ' + CODE;
  }

  function walk(root) {
    if (!root) return;

    if (root.nodeType === 3) {
      processTextNode(root);
      return;
    }
    if (root.nodeType !== 1 || isSkipped(root)) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.nodeValue && node.nodeValue.indexOf(SYMBOL) !== -1
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    // Collect before mutating so the walker isn't operating on a moving target.
    var found = [];
    var node;
    while ((node = walker.nextNode())) found.push(node);
    found.forEach(processTextNode);
  }

  var queue = [];
  var scheduled = false;

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];

      if (mutation.type === 'characterData') {
        queue.push(mutation.target);
        continue;
      }
      for (var j = 0; j < mutation.addedNodes.length; j++) {
        queue.push(mutation.addedNodes[j]);
      }
    }
    if (queue.length && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  function observe() {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  function flush() {
    scheduled = false;
    var batch = queue;
    queue = [];

    // Detach while we write so our own edits don't churn back through the queue.
    observer.disconnect();
    batch.forEach(walk);
    observe();
  }

  function init() {
    walk(document.body);
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
